import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/apiClient';

import {
  changeStatus,
  createInvitation,
  listCoaches,
  listInvitations,
  listStudents,
  revokeInvitation,
  getStudent,
} from './api';

export const INVITATIONS_KEY = ['relationships', 'invitations'] as const;
export const STUDENTS_KEY = ['relationships', 'students'] as const;
export const COACHES_KEY = ['relationships', 'coaches'] as const;

const retry = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 2;
};

export function useInvitations() {
  return useQuery({ queryKey: INVITATIONS_KEY, queryFn: listInvitations, retry });
}

export function useStudents() {
  return useQuery({ queryKey: STUDENTS_KEY, queryFn: listStudents, retry });
}

export function useCoaches() {
  return useQuery({ queryKey: COACHES_KEY, queryFn: listCoaches, retry });
}

export function useStudent(relationshipId: string) {
  return useQuery({
    queryKey: [...STUDENTS_KEY, relationshipId],
    queryFn: () => getStudent(relationshipId),
    retry,
  });
}

/**
 * O convite criado **não** entra no cache junto com a lista.
 *
 * A resposta carrega o token, e o cache do TanStack Query sobrevive a trocas de
 * tela. Guardá-lo ali significaria manter um segredo de uso único em memória
 * depois de a tela que o mostrou ter sido fechada. A lista é recarregada do
 * servidor, que devolve tudo menos o token.
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hint: string | null) => createInvitation(hint),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY }),
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY }),
  });
}

export function useChangeStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      relationshipId,
      status,
    }: {
      relationshipId: string;
      status: 'active' | 'paused' | 'ended';
    }) => changeStatus(relationshipId, status),
    // Invalida as duas listas: a mesma mudança aparece para os dois lados, e
    // quem tem os dois papéis vê as duas telas.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STUDENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: COACHES_KEY });
    },
  });
}

/**
 * A chave de tradução da recusa.
 *
 * `invitation_invalid` cobre token inexistente, expirado, revogado e já usado —
 * de propósito, e a tela não tenta adivinhar qual foi. Dizer "expirado" quando
 * o token nunca existiu confirmaria a existência dos que existiram.
 */
export function relationshipErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'invitation_invalid') return 'relationships:invite.invalid';
    if (error.code === 'relationship_inactive') return 'relationships:error.inactive';
    const rule = error.details.rule;
    if (typeof rule === 'string') return `relationships:rule.${rule}`;
  }
  return 'relationships:error.unknown';
}
